import { ContentValidationError } from "./parser";
import type {
  DialogueCatalogDocument,
  DialogueId,
  DialogueStepDocument,
  EntityDefinitionDocument,
  EntityTypeId,
  GameContentDocuments,
  LevelDocument,
  LevelId,
  TileDocument,
  WorldMapEdgeDocument,
  WorldMapNodeDocument,
} from "./types";

const EMPTY_EDGES: readonly WorldMapEdgeDocument[] = Object.freeze([]);

export interface ContentDocumentLocations {
  readonly manifest: string;
  readonly worldMap: string;
  readonly globalDialogues: string;
  readonly tiles: string;
  readonly entities: ReadonlyMap<EntityTypeId, string>;
  readonly levels: ReadonlyMap<LevelId, string>;
}

/** Immutable, fully cross-referenced authored content used by game systems and the editor. */
export class ContentRepository {
  readonly documents: GameContentDocuments;
  readonly manifest;
  readonly worldMap;
  readonly globalDialogues;
  readonly tileCatalog;

  private readonly levelsById: ReadonlyMap<LevelId, LevelDocument>;
  private readonly entitiesById: ReadonlyMap<
    EntityTypeId,
    EntityDefinitionDocument
  >;
  private readonly nodesById: ReadonlyMap<LevelId, WorldMapNodeDocument>;
  private readonly tilesById: ReadonlyMap<number, TileDocument>;
  private readonly outgoingById: ReadonlyMap<
    LevelId,
    readonly WorldMapEdgeDocument[]
  >;
  private readonly dialoguesByLevel: ReadonlyMap<
    LevelId,
    ReadonlyMap<DialogueId, readonly DialogueStepDocument[]>
  >;
  private readonly globalDialoguesById: ReadonlyMap<
    DialogueId,
    readonly DialogueStepDocument[]
  >;
  private readonly campaignLevelList: readonly LevelDocument[];
  private readonly initialIds: readonly LevelId[];
  private readonly tutorialLevel: LevelDocument;
  private readonly survivalLevel: LevelDocument;

  constructor(
    documents: GameContentDocuments,
    locations?: ContentDocumentLocations,
  ) {
    this.documents = deepFreeze(documents);
    this.manifest = this.documents.manifest;
    this.worldMap = this.documents.worldMap;
    this.globalDialogues = this.documents.globalDialogues;
    this.tileCatalog = this.documents.tiles;

    this.levelsById = uniqueMap(
      this.documents.levels,
      "level",
      (index) => locationOfLevel(locations, this.documents.levels[index].id),
      "$.id",
    );
    this.entitiesById = uniqueMap(
      this.documents.entities,
      "entity",
      (index) => locationOfEntity(locations, this.documents.entities[index].id),
      "$.id",
    );
    this.nodesById = uniqueMap(
      this.worldMap.nodes,
      "world-map node",
      () => locations?.worldMap ?? "<content>",
      "$.nodes",
    );
    this.tilesById = uniqueNumericMap(
      this.tileCatalog.tiles,
      locations?.tiles ?? "<content>",
    );

    const outgoing = new Map<LevelId, readonly WorldMapEdgeDocument[]>();
    const mutableOutgoing = new Map<LevelId, WorldMapEdgeDocument[]>();
    for (const edge of this.worldMap.edges) {
      const edges = mutableOutgoing.get(edge.from) ?? [];
      edges.push(edge);
      mutableOutgoing.set(edge.from, edges);
    }
    for (const [id, edges] of mutableOutgoing)
      outgoing.set(id, Object.freeze(edges));
    this.outgoingById = outgoing;

    this.globalDialoguesById = dialogueMap(this.globalDialogues);
    const levelDialogues = new Map<
      LevelId,
      ReadonlyMap<DialogueId, readonly DialogueStepDocument[]>
    >();
    for (const level of this.documents.levels)
      levelDialogues.set(level.id, dialogueMap(level.dialogues));
    this.dialoguesByLevel = levelDialogues;

    this.validateReferences(locations);
    this.campaignLevelList = Object.freeze(
      this.worldMap.nodes.map((node) => this.requireLevel(node.id)),
    );
    this.initialIds = Object.freeze(
      this.worldMap.nodes
        .filter((node) => node.initial === true)
        .map((node) => node.id),
    );
    this.tutorialLevel = this.requireUniqueKind("tutorial");
    this.survivalLevel = this.requireUniqueKind("survival");
  }

  level(id: LevelId): LevelDocument {
    return this.requireLevel(id);
  }

  hasLevel(id: LevelId): boolean {
    return this.levelsById.has(id);
  }

  entity(id: EntityTypeId): EntityDefinitionDocument {
    const entity = this.entitiesById.get(id);
    if (!entity) throw new Error(`unknown entity '${id}'`);
    return entity;
  }

  hasEntity(id: EntityTypeId): boolean {
    return this.entitiesById.has(id);
  }

  node(id: LevelId): WorldMapNodeDocument {
    const node = this.nodesById.get(id);
    if (!node) throw new Error(`unknown world-map node '${id}'`);
    return node;
  }

  hasNode(id: LevelId): boolean {
    return this.nodesById.has(id);
  }

  tile(id: number): TileDocument {
    const tile = this.tilesById.get(id);
    if (!tile) throw new Error(`unknown tile '${id}'`);
    return tile;
  }

  hasTile(id: number): boolean {
    return this.tilesById.has(id);
  }

  outgoing(id: LevelId): readonly WorldMapEdgeDocument[] {
    return this.outgoingById.get(id) ?? EMPTY_EDGES;
  }

  dialogue(
    levelId: LevelId | null,
    id: DialogueId,
  ): readonly DialogueStepDocument[] {
    const local =
      levelId === null
        ? undefined
        : this.dialoguesByLevel.get(levelId)?.get(id);
    const script = local ?? this.globalDialoguesById.get(id);
    if (!script) {
      const scope =
        levelId === null ? "global dialogue" : `dialogue in '${levelId}'`;
      throw new Error(`unknown ${scope} '${id}'`);
    }
    return script;
  }

  hasDialogue(levelId: LevelId | null, id: DialogueId): boolean {
    return (
      (levelId !== null &&
        this.dialoguesByLevel.get(levelId)?.has(id) === true) ||
      this.globalDialoguesById.has(id)
    );
  }

  get campaignLevels(): readonly LevelDocument[] {
    return this.campaignLevelList;
  }

  get initialLevelIds(): readonly LevelId[] {
    return this.initialIds;
  }

  get tutorial(): LevelDocument {
    return this.tutorialLevel;
  }

  get survival(): LevelDocument {
    return this.survivalLevel;
  }

  private validateReferences(locations?: ContentDocumentLocations): void {
    const worldFile = locations?.worldMap ?? "<content>";
    this.validateGraph(worldFile);
    const shotChannels = this.validateShotChannels(locations);
    for (const entity of this.documents.entities) {
      const file = locationOfEntity(locations, entity.id);
      this.validateEntityBehavior(entity.behaviorData, shotChannels, file, "$");
      for (const [variantId, variant] of Object.entries(
        entity.variants ?? {},
      )) {
        this.validateEntityBehavior(
          variant.behaviorData,
          shotChannels,
          file,
          `$.variants[${JSON.stringify(variantId)}]`,
        );
      }
    }
    for (const [dialogueId, steps] of Object.entries(this.globalDialogues)) {
      for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
        if (steps[stepIndex].encounter !== undefined) {
          fail(
            locations?.globalDialogues ?? "<content>",
            `$[${JSON.stringify(dialogueId)}][${stepIndex}].encounter`,
            "global dialogues cannot reference a level-local encounter",
          );
        }
      }
    }

    for (
      let levelIndex = 0;
      levelIndex < this.documents.levels.length;
      levelIndex++
    ) {
      const level = this.documents.levels[levelIndex];
      const file = locationOfLevel(locations, level.id);
      const encounterIds = new Set(level.encounters.map((entry) => entry.id));
      for (const [dialogueId, steps] of Object.entries(level.dialogues)) {
        for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
          const encounter = steps[stepIndex].encounter;
          if (encounter !== undefined && !encounterIds.has(encounter)) {
            fail(
              file,
              `$.dialogues[${JSON.stringify(dialogueId)}][${stepIndex}].encounter`,
              `references unknown encounter '${encounter}'`,
            );
          }
        }
      }
      const generatorIds = new Set<string>();
      for (let index = 0; index < (level.generators?.length ?? 0); index++) {
        const generator = level.generators![index];
        if (generatorIds.has(generator.id)) {
          fail(
            file,
            `$.generators[${index}].id`,
            `duplicate generator id '${generator.id}'`,
          );
        }
        generatorIds.add(generator.id);
      }
      if (generatorIds.size > 0) {
        const generatorEntity = this.entitiesById.get("generator");
        if (generatorEntity?.behavior !== "generator") {
          fail(
            file,
            "$.generators",
            "requires the authored 'generator' entity definition",
          );
        }
      }

      for (let row = 0; row < level.environment.tiles.length; row++) {
        for (
          let column = 0;
          column < level.environment.tiles[row].length;
          column++
        ) {
          const tileId = level.environment.tiles[row][column];
          if (tileId !== null && !this.tilesById.has(tileId)) {
            fail(
              file,
              `$.environment.tiles[${row}][${column}]`,
              `references unknown tile '${tileId}'`,
            );
          }
        }
      }
      const cellColumns =
        level.environment.tiles[0].length *
        (this.tileCatalog.tileSize / this.tileCatalog.cellSize);
      const cellRows =
        level.environment.tiles.length *
        (this.tileCatalog.tileSize / this.tileCatalog.cellSize);
      this.requireLevelPoint(
        level.start,
        cellColumns,
        cellRows,
        file,
        "$.start",
      );
      for (let index = 0; index < (level.blocks?.length ?? 0); index++) {
        this.requireLevelPoint(
          level.blocks![index].at,
          cellColumns,
          cellRows,
          file,
          `$.blocks[${index}].at`,
        );
      }
      for (let index = 0; index < (level.generators?.length ?? 0); index++) {
        this.requireLevelPoint(
          level.generators![index].at,
          cellColumns,
          cellRows,
          file,
          `$.generators[${index}].at`,
        );
      }
      for (let index = 0; index < (level.targets?.length ?? 0); index++) {
        this.requireLevelPoint(
          level.targets![index].at,
          cellColumns,
          cellRows,
          file,
          `$.targets[${index}].at`,
        );
      }
      for (let index = 0; index < (level.pickups?.length ?? 0); index++) {
        this.requireLevelPoint(
          level.pickups![index].at,
          cellColumns,
          cellRows,
          file,
          `$.pickups[${index}].at`,
        );
      }
      for (let index = 0; index < (level.obstacles?.length ?? 0); index++) {
        const obstacle = level.obstacles![index];
        if (obstacle.to.x > cellColumns || obstacle.to.y > cellRows) {
          fail(
            file,
            `$.obstacles[${index}].to`,
            `lies outside the ${cellColumns} by ${cellRows} level grid`,
          );
        }
        if (
          obstacle.bucket !== undefined &&
          (obstacle.bucket.x >= level.environment.tiles[0].length ||
            obstacle.bucket.y >= level.environment.tiles.length)
        ) {
          fail(
            file,
            `$.obstacles[${index}].bucket`,
            "references a background tile outside the level",
          );
        }
      }

      for (
        let encounterIndex = 0;
        encounterIndex < level.encounters.length;
        encounterIndex++
      ) {
        const entry = level.encounters[encounterIndex];
        if (
          entry.onClear?.activate !== undefined &&
          !encounterIds.has(entry.onClear.activate)
        ) {
          fail(
            file,
            `$.encounters[${encounterIndex}].onClear.activate`,
            `references unknown encounter '${entry.onClear.activate}'`,
          );
        }
        if (entry.onClear?.dialogue !== undefined)
          this.requireDialogueReference(
            level,
            entry.onClear.dialogue,
            file,
            `$.encounters[${encounterIndex}].onClear.dialogue`,
          );
        for (
          let barrierIndex = 0;
          barrierIndex < (entry.barriers?.length ?? 0);
          barrierIndex++
        ) {
          this.requireLevelPoint(
            entry.barriers![barrierIndex].at,
            cellColumns,
            cellRows,
            file,
            `$.encounters[${encounterIndex}].barriers[${barrierIndex}].at`,
          );
        }
        for (
          let spawnIndex = 0;
          spawnIndex < (entry.spawns?.length ?? 0);
          spawnIndex++
        ) {
          const spawn = entry.spawns![spawnIndex];
          const entity = this.entitiesById.get(spawn.entity);
          const spawnPath = `$.encounters[${encounterIndex}].spawns[${spawnIndex}]`;
          if (!entity)
            fail(
              file,
              `${spawnPath}.entity`,
              `references unknown entity '${spawn.entity}'`,
            );
          if (entity.behavior === "generator") {
            fail(
              file,
              `${spawnPath}.entity`,
              "generator entities must be authored through $.generators",
            );
          }
          this.requireLevelPoint(
            spawn.at,
            cellColumns,
            cellRows,
            file,
            `${spawnPath}.at`,
          );
        }
      }

      for (
        let triggerIndex = 0;
        triggerIndex < level.triggers.length;
        triggerIndex++
      ) {
        const entry = level.triggers[triggerIndex];
        if (
          entry.encounter !== undefined &&
          !encounterIds.has(entry.encounter)
        ) {
          fail(
            file,
            `$.triggers[${triggerIndex}].encounter`,
            `references unknown encounter '${entry.encounter}'`,
          );
        }
        if (entry.dialogue !== undefined)
          this.requireDialogueReference(
            level,
            entry.dialogue,
            file,
            `$.triggers[${triggerIndex}].dialogue`,
          );
        this.requireLevelPoint(
          entry.at,
          cellColumns,
          cellRows,
          file,
          `$.triggers[${triggerIndex}].at`,
        );
      }

      if (level.startDialogue !== undefined)
        this.requireDialogueReference(
          level,
          level.startDialogue,
          file,
          "$.startDialogue",
        );
      if (level.boss !== undefined) {
        this.requireLevelPoint(
          level.boss.at,
          cellColumns,
          cellRows,
          file,
          "$.boss.at",
        );
        const entity = this.entitiesById.get(level.boss.entity);
        if (!entity)
          fail(
            file,
            "$.boss.entity",
            `references unknown entity '${level.boss.entity}'`,
          );
        if (entity.behavior !== "boss") {
          fail(
            file,
            "$.boss.entity",
            `entity '${level.boss.entity}' does not have boss behavior`,
          );
        }
        const variant = entity.variants?.[level.boss.variant];
        if (variant === undefined)
          fail(
            file,
            "$.boss.variant",
            `references unknown variant '${level.boss.variant}' on entity '${level.boss.entity}'`,
          );
        if (
          variant.behaviorData === undefined ||
          !("rig" in variant.behaviorData)
        ) {
          fail(
            file,
            "$.boss.variant",
            `variant '${level.boss.variant}' has no boss behavior data`,
          );
        }
        if (level.boss.weaponUnlockDialogue !== undefined) {
          this.requireDialogueReference(
            level,
            level.boss.weaponUnlockDialogue,
            file,
            "$.boss.weaponUnlockDialogue",
          );
        }
        if (level.boss.lowHealthDialogue !== undefined)
          this.requireDialogueReference(
            level,
            level.boss.lowHealthDialogue,
            file,
            "$.boss.lowHealthDialogue",
          );
        if (level.boss.defeatedDialogue !== undefined)
          this.requireDialogueReference(
            level,
            level.boss.defeatedDialogue,
            file,
            "$.boss.defeatedDialogue",
          );
      }
      if (level.survival !== undefined) {
        level.survival.loop.forEach((id, index) => {
          if (!encounterIds.has(id))
            fail(
              file,
              `$.survival.loop[${index}]`,
              `references unknown encounter '${id}'`,
            );
        });
        level.survival.debriefs.forEach((entry, index) =>
          this.requireDialogueReference(
            level,
            entry.dialogue,
            file,
            `$.survival.debriefs[${index}].dialogue`,
          ),
        );
      }
    }
  }

  private requireLevelPoint(
    point: { readonly x: number; readonly y: number },
    width: number,
    height: number,
    file: string,
    path: string,
  ): void {
    if (point.x >= width || point.y >= height) {
      fail(file, path, `lies outside the ${width} by ${height} level grid`);
    }
  }

  private validateEntityBehavior(
    behaviorData: EntityDefinitionDocument["behaviorData"],
    shotChannels: ReadonlySet<string>,
    file: string,
    path: string,
  ): void {
    if (behaviorData === undefined) return;
    if ("projectilePattern" in behaviorData) {
      if (!shotChannels.has(behaviorData.channel)) {
        fail(
          file,
          `${path}.behaviorData.channel`,
          `references unknown shot channel '${behaviorData.channel}'`,
        );
      }
      return;
    }
    for (const [actionId, action] of Object.entries(behaviorData.actions)) {
      if (
        action.shot !== undefined &&
        behaviorData.patterns[action.shot.pattern] === undefined
      ) {
        fail(
          file,
          `${path}.behaviorData.actions[${JSON.stringify(actionId)}].shot.pattern`,
          `references unknown boss projectile pattern '${action.shot.pattern}'`,
        );
      }
      if (
        action.loopTo !== undefined &&
        behaviorData.actions[String(action.loopTo + 1)] === undefined
      ) {
        fail(
          file,
          `${path}.behaviorData.actions[${JSON.stringify(actionId)}].loopTo`,
          `resumes at unknown boss action '${action.loopTo + 1}'`,
        );
      }
    }
  }

  private validateShotChannels(
    locations?: ContentDocumentLocations,
  ): ReadonlySet<string> {
    const survival = this.documents.levels.find(
      (level) => level.kind === "survival",
    );
    if (survival?.survival === undefined) return new Set();
    const file = locationOfLevel(locations, survival.id);
    const baseline = survival.survival.shotWaves[0].channels.map(
      (channel) => channel.id,
    );
    for (
      let waveIndex = 1;
      waveIndex < survival.survival.shotWaves.length;
      waveIndex++
    ) {
      const channels = survival.survival.shotWaves[waveIndex].channels;
      if (
        channels.length !== baseline.length ||
        channels.some((channel, index) => channel.id !== baseline[index])
      ) {
        fail(
          file,
          `$.survival.shotWaves[${waveIndex}].channels`,
          "must contain the same ordered channel ids as the first shot wave",
        );
      }
    }
    return new Set(baseline);
  }

  private validateGraph(worldFile: string): void {
    for (let index = 0; index < this.worldMap.nodes.length; index++) {
      const node = this.worldMap.nodes[index];
      const level = this.levelsById.get(node.id);
      if (!level)
        fail(
          worldFile,
          `$.nodes[${index}].id`,
          `references unknown level '${node.id}'`,
        );
      if (level.kind !== "mission")
        fail(
          worldFile,
          `$.nodes[${index}].id`,
          `campaign node references non-mission level '${node.id}'`,
        );
    }
    for (let index = 0; index < this.worldMap.edges.length; index++) {
      const edge = this.worldMap.edges[index];
      if (!this.nodesById.has(edge.from))
        fail(
          worldFile,
          `$.edges[${index}].from`,
          `references unknown node '${edge.from}'`,
        );
      if (!this.nodesById.has(edge.to))
        fail(
          worldFile,
          `$.edges[${index}].to`,
          `references unknown node '${edge.to}'`,
        );
    }

    if (!this.worldMap.nodes.some((node) => node.initial === true)) {
      fail(worldFile, "$.nodes", "graph has no initial node");
    }
    for (
      let index = 0;
      index < this.worldMap.milestoneUnlocks.length;
      index++
    ) {
      const unlock = this.worldMap.milestoneUnlocks[index];
      if (
        unlock.unlockLevel !== undefined &&
        !this.levelsById.has(unlock.unlockLevel)
      ) {
        fail(
          worldFile,
          `$.milestoneUnlocks[${index}].unlockLevel`,
          `references unknown level '${unlock.unlockLevel}'`,
        );
      }
      if (
        unlock.unlockSurvival === true &&
        !this.documents.levels.some((level) => level.kind === "survival")
      ) {
        fail(
          worldFile,
          `$.milestoneUnlocks[${index}].unlockSurvival`,
          "requires a survival level",
        );
      }
    }
  }

  private requireDialogueReference(
    level: LevelDocument,
    id: DialogueId,
    file: string,
    path: string,
  ): void {
    if (!this.hasDialogue(level.id, id))
      fail(file, path, `references unknown dialogue '${id}'`);
  }

  private requireLevel(id: LevelId): LevelDocument {
    const level = this.levelsById.get(id);
    if (!level) throw new Error(`unknown level '${id}'`);
    return level;
  }

  private requireUniqueKind(kind: "tutorial" | "survival"): LevelDocument {
    let found: LevelDocument | undefined;
    for (const level of this.documents.levels) {
      if (level.kind !== kind) continue;
      if (found) throw new Error(`content has multiple ${kind} levels`);
      found = level;
    }
    if (!found) throw new Error(`content has no ${kind} level`);
    return found;
  }
}

function uniqueMap<T extends { readonly id: string }>(
  values: readonly T[],
  label: string,
  filename: (index: number) => string,
  path: string,
): ReadonlyMap<string, T> {
  const result = new Map<string, T>();
  values.forEach((value, index) => {
    if (result.has(value.id))
      fail(filename(index), path, `duplicate ${label} id '${value.id}'`);
    result.set(value.id, value);
  });
  return result;
}

function uniqueNumericMap(
  values: readonly TileDocument[],
  filename: string,
): ReadonlyMap<number, TileDocument> {
  const result = new Map<number, TileDocument>();
  values.forEach((value, index) => {
    if (result.has(value.id))
      fail(filename, `$.tiles[${index}].id`, `duplicate tile id '${value.id}'`);
    result.set(value.id, value);
  });
  return result;
}

function dialogueMap(
  catalog: DialogueCatalogDocument,
): ReadonlyMap<DialogueId, readonly DialogueStepDocument[]> {
  return new Map(Object.entries(catalog));
}

function locationOfLevel(
  locations: ContentDocumentLocations | undefined,
  id: LevelId,
): string {
  return locations?.levels.get(id) ?? "<content>";
}

function locationOfEntity(
  locations: ContentDocumentLocations | undefined,
  id: EntityTypeId,
): string {
  return locations?.entities.get(id) ?? "<content>";
}

function fail(filename: string, path: string, message: string): never {
  throw new ContentValidationError(filename, path, message);
}

function deepFreeze<T>(value: T): T {
  if (value === null || typeof value !== "object" || Object.isFrozen(value))
    return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}
